import React from "react";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableBody from "@material-ui/core/TableBody";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { styled } from "@material-ui/styles";
import { TableCellCustom } from "../Table/TableCell/TableCell";
import { EnhancedTable } from "../Table/EnhancedTable/EnhancedTable";
import { replaceUnderscoreAndCapitalize } from "../../utils";
import reorder from "../../utils/helpers/reorder";
import { NormalizersReadme } from "./NormalizersReadme/NormalizersReadme";
import { NormalizersOptions } from "./NormalizersOptions/NormalizersOptions";
import { NormalizersActions } from "./NormalizersActions/NormalizersActions";
import {Checkbox} from "../Inputs/Checkbox/Checkbox";
import {useTableItemsSelection} from "../../hooks/table";
import {dataNormalizersSelector} from "../../modules/ConfigManager/services/Entities/selectors";
import {
    setSelectedDataNormalizers,
} from "../../modules/ConfigManager/services/Entities/actions";

const Row = styled(TableRow)((props) => props?.snapshot?.isDragging ? { display: "table" } : {});

export const NormalizersTable = ({
    dataNormalizers,
    reorderDataNormalizer,
    ...restProps
}) => {

    const formatCode = (code) => {
        const codeSlice =  code.replace("normalizer.", "");

        return (
            replaceUnderscoreAndCapitalize(codeSlice , "-", " ")
        );
    };

    const onDragEnd = (result) => {
        if (
            !result.destination ||
            result.destination.index === result.source.index
        ) {
            return;
        }

        // no movement
        if (result.destination.index === result.source.index) {
            return;
        }

        const naeDataSelector = reorder(
            dataNormalizers,
            result.source.index,
            result.destination.index
        );

        reorderDataNormalizer(naeDataSelector);
    };

    const {
        handleSelectItem,
        handleSelectAll,
        isItemSelected,
        isSelectedAll
    } = useTableItemsSelection(dataNormalizersSelector, setSelectedDataNormalizers);

    const ids = Array.from({ length: dataNormalizers.length }, (_, i) => i);

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <EnhancedTable
                className={"templates-configs-table"}
                count = {dataNormalizers?.length || 0}
            >
                <TableHead>
                    <TableRow>
                        <TableCellCustom>
                            <Checkbox
                                value={isSelectedAll(ids)}
                                onChange={() => handleSelectAll(ids)}
                            />
                        </TableCellCustom>
                        <TableCellCustom>Name</TableCellCustom>
                        <TableCellCustom>Options</TableCellCustom>
                        <TableCellCustom>Readme</TableCellCustom>
                        <TableCellCustom isContentWidth>Actions</TableCellCustom>
                    </TableRow>
                </TableHead>

                <Droppable droppableId="table">
                    {(droppableProvided) => (
                        <TableBody
                            ref={(ref) => {
                                droppableProvided.innerRef(ref);
                            }}
                        >
                            {
                                dataNormalizers?.length ? dataNormalizers.map((normalizer, index) => (
                                    <Draggable
                                        draggableId={`${normalizer.code}${index}`}
                                        index={index}
                                        key={`${normalizer.code}${index}`}
                                    >
                                        {(provided, snapshot) => (
                                            <Row
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                ref={provided.innerRef}
                                                key={index}
                                                provided={provided}
                                                snapshot={snapshot}
                                                hover
                                            >
                                                <TableCellCustom>
                                                    <Checkbox
                                                        value={isItemSelected(index)}
                                                        onChange={() => handleSelectItem(index)}
                                                    />
                                                </TableCellCustom>
                                                <TableCellCustom>
                                                    <b>{normalizer?.name || (normalizer?.code && formatCode(normalizer.code))}</b>
                                                </TableCellCustom>

                                                <TableCellCustom>
                                                    {
                                                        normalizer?.options && (
                                                            <NormalizersOptions options={normalizer.options}/>
                                                        )
                                                    }
                                                </TableCellCustom>

                                                <TableCellCustom>
                                                    <NormalizersReadme normalizer={normalizer}/>
                                                </TableCellCustom>

                                                <TableCellCustom>
                                                    <NormalizersActions
                                                        index={index}
                                                        {...restProps}
                                                    />
                                                </TableCellCustom>
                                            </Row>
                                        )}
                                    </Draggable>
                                )) : null }
                            {droppableProvided.placeholder}
                        </TableBody>
                    )}
                </Droppable>
            </EnhancedTable>
        </DragDropContext>
    );
};
